import { buildPromptPayPayload, normalizePromptPayAccountNumber } from './promptpay';
import { buildTemplatePayload, normalizeConvention } from './thaiQr';

// Values of branch.setting.show_qr_code_payment. 0 and 1 are the original,
// stored values and keep their print-only meaning. 2-4 are additive options.
// The backend stores the setting verbatim (no enum validation), so no
// migration is needed for the new value.
export const QR_SETTING = {
  QUOTATION_INVOICE: 0, // print QR on quotation + invoice
  QUOTATION_INVOICE_RECEIPT: 1, // ...and on receipt
  CUSTOMER_DISPLAY_ONLY: 2, // never printed; shown on the Customer Display only
  QUOTATION_INVOICE_AND_DISPLAY: 3,
  QUOTATION_INVOICE_RECEIPT_AND_DISPLAY: 4
};

// Document type ids as stored in the backend (document_type_dict).
const DOC = { QUOTATION: 0, INVOICE: 1, RECEIPT: 2 };

// Whether printed documents carry the payment QR. Replaces the inline
// condition previously duplicated in the print components; behaviour for the
// stored values 0 and 1 is unchanged.
export function isPaymentQrPrinted(docTypeId, setting) {
  const type = Number(docTypeId);
  const value = Number(setting);
  if (value === QR_SETTING.CUSTOMER_DISPLAY_ONLY) return false;
  return type < DOC.RECEIPT ||
    (type === DOC.RECEIPT &&
      (value === QR_SETTING.QUOTATION_INVOICE_RECEIPT || value === QR_SETTING.QUOTATION_INVOICE_RECEIPT_AND_DISPLAY));
}

// Whether the Customer Display may show a payment QR for this document type.
// Debit/credit notes never carry a QR today (same as print), so they are
// excluded here too.
export function isPaymentQrOnCustomerDisplay(docTypeId, setting) {
  const value = Number(setting);
  return Number(docTypeId) <= DOC.RECEIPT &&
    (value === QR_SETTING.CUSTOMER_DISPLAY_ONLY ||
      value === QR_SETTING.QUOTATION_INVOICE_AND_DISPLAY && Number(docTypeId) < DOC.RECEIPT ||
      value === QR_SETTING.QUOTATION_INVOICE_RECEIPT_AND_DISPLAY);
}

const SNAPSHOT_MODES = ['promptpay', 'image', 'template', 'none'];

function text(value) {
  return String(value === null || value === undefined ? '' : value).trim();
}

// Mirrors backend normalize_document_qr_payment_snapshot.
function normalizeSnapshot(snapshot) {
  if (!snapshot || typeof snapshot !== 'object' || Array.isArray(snapshot)) return null;
  const mode = text(snapshot.mode).toLowerCase();
  const normalized = {
    mode: SNAPSHOT_MODES.indexOf(mode) === -1 ? 'none' : mode,
    bank_name: '',
    account_name: '',
    account_number: '',
    image_url: ''
  };
  if (SNAPSHOT_MODES.indexOf(mode) === -1) return normalized;
  normalized.bank_name = text(snapshot.bank_name);
  normalized.account_name = text(snapshot.account_name);
  normalized.account_number = normalizePromptPayAccountNumber(snapshot.account_number);
  normalized.image_url = text(snapshot.image_url);
  if (mode === 'template') {
    normalized.template_id = text(snapshot.template_id);
    normalized.template_payload = text(snapshot.template_payload);
    normalized.template_convention = normalizeConvention(snapshot.template_convention);
  }
  return normalized;
}

// Mirrors backend document_qr_payment_render_data for the fields the display
// needs. Returns null when nothing payable should be shown, else
// { qr: {kind:'promptpay', payload} | {kind:'image', url}, accountName, bankName }.
// `amount` is the SAVED net price.
export function resolvePaymentQr(snapshot, amount, legacyImageUrl) {
  const normalized = normalizeSnapshot(snapshot);
  if (normalized === null) {
    // Documents without a snapshot keep the legacy Branch image behaviour.
    const url = text(legacyImageUrl);
    return url ? { qr: { kind: 'image', url }, accountName: '', bankName: '' } : null;
  }
  if (normalized.mode === 'image') {
    return normalized.image_url
      ? { qr: { kind: 'image', url: normalized.image_url }, accountName: '', bankName: '' }
      : null;
  }
  if (normalized.mode === 'promptpay') {
    const payload = buildPromptPayPayload(normalized.account_number, amount);
    if (!payload) return null;
    return {
      qr: { kind: 'promptpay', payload },
      accountName: normalized.account_name,
      bankName: normalized.bank_name
    };
  }
  if (normalized.mode === 'template') {
    // An uploaded merchant QR with this bill's amount put in (same output as the backend).
    const payload = buildTemplatePayload(
      normalized.template_payload,
      amount,
      normalized.template_convention
    );
    if (!payload) return null;
    // The payee text the owner typed for this QR (empty when none).
    return { qr: { kind: 'promptpay', payload }, accountName: normalized.account_name, bankName: '' };
  }
  return null;
}
